import {
    ArcElement,
    Chart as ChartJS,
    Tooltip,
    Legend
} from 'chart.js';
import { useEffect, useRef, useState } from 'react';
import { Col, Row, Select, Spin } from 'antd';
import { toast } from 'react-toastify';

import { thongKeNhanSuTheoChucDanh } from '../../services/nhanvien';
import { ThongKeNhanSuResponse } from '@app/models/nhanvien/NhanVien';

ChartJS.register(ArcElement, Tooltip, Legend);

const { Option } = Select;

const COLORS = [
  '#9DBDF9',
  '#9FE3CC',
  '#A3AEC9',
  '#F9D37F',
  '#A79AF7',
  '#A9E6F9',
  '#B89AD6',
  '#F9BC8C',
  '#73C6C0',
  '#F6B7D2'
];

const ThongKeNhanSuChucDanhTrangChu = () => {
    const chartRef = useRef<HTMLCanvasElement>(null);

    const chartInstance = useRef<
        ChartJS<'doughnut', number[], string> | null
    >(null);

    const [isLoading, setIsLoading] = useState(true);
    const [rawData, setRawData] = useState<ThongKeNhanSuResponse[]>([]);
    const [selectedDonVi, setSelectedDonVi] = useState<number>(0);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const result = await thongKeNhanSuTheoChucDanh();
            setRawData(result);
        } catch {
            toast.error('Không thể tải dữ liệu thống kê!');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const dsDonVi = Array.from(
        new Map(rawData.map((d) => [d.idDonVi, d.tenDonVi])).entries()
    ).map(([id, ten]) => ({ id, ten }));

    const filteredData =
        selectedDonVi === 0
            ? rawData
            : rawData.filter((d) => d.idDonVi === selectedDonVi);

    const chartData = Object.values(
        filteredData.reduce(
            (acc, cur) => {
                const key = cur.tenChucVu as string;
                if (!acc[key]) {
                    acc[key] = {
                        tenChucVu: key,
                        soLuongNhanVien: 0
                    };
                }

                acc[key].soLuongNhanVien += cur.soLuongNhanVien;

                return acc;
            },
            {} as Record<string, { tenChucVu: string; soLuongNhanVien: number }>
        )
    );

    const tongSo = chartData.reduce(
        (s, d) => s + d.soLuongNhanVien,
        0
    );

    useEffect(() => {
        if (!chartRef.current || chartData.length === 0) return;

        chartInstance.current?.destroy();

        chartInstance.current = new ChartJS(chartRef.current, {
            type: 'doughnut',

            data: {
                labels: chartData.map((d) => d.tenChucVu),

                datasets: [
                    {
                        data: chartData.map((d) => d.soLuongNhanVien),
                        backgroundColor: chartData.map(
                            (_, i) => COLORS[i % COLORS.length]
                        ),
                        borderWidth: 1
                    }
                ]
            },

            options: {
                responsive: true,
                maintainAspectRatio: false,

                plugins: {
                    legend: {
                        display: false
                    },

                    tooltip: {
                        callbacks: {
                            label: (item) => {
                                const d = chartData[item.dataIndex];

                                const pct =
                                    tongSo > 0
                                        ? ((d.soLuongNhanVien / tongSo) * 100).toFixed(1)
                                        : '0.0';

                                return `${d.tenChucVu}: ${d.soLuongNhanVien} (${pct}%)`;
                            }
                        }
                    }
                }
            }
        });

        return () => {
            chartInstance.current?.destroy();
        };
    }, [chartData]);

    const cardStyle: React.CSSProperties = {
        background: '#f5f7fb',
        borderRadius: 8,
        padding: '10px 18px',
        textAlign: 'center',
        minWidth: 120
    };

    return (
        <Col xs={24} xl={24} style={{ marginTop: 16 }}>
            <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 10 }}
            >
                <Col>
                    <b style={{ fontSize: 20, color: '#071C50' }}>
                        Thống kê nhân sự theo chức danh
                    </b>
                </Col>

                <Col>
                    <Select
                        style={{ width: 220 }}
                        value={selectedDonVi}
                        onChange={(val) => setSelectedDonVi(val)}
                        size="small"
                    >
                        <Option value={0}>Tất cả đơn vị</Option>

                        {dsDonVi.map((dv) => (
                            <Option key={dv.id} value={dv.id}>
                                {dv.ten}
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            <div className="card" style={{ padding: 20 }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: 60 }}>
                        <Spin tip="Đang tải dữ liệu..." />
                    </div>
                ) : chartData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60 }}>
                        Không có dữ liệu
                    </div>
                ) : (
                        <div style={{ display: 'flex', gap: 24, alignItems: 'stretch' }}>

                            <div style={{ flex: '0 0 50%', height: 320 }}>
                                <canvas ref={chartRef} />
                            </div>

                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ ...cardStyle, flex: 1 }}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Tổng nhân viên</div>
                                        <div style={{ fontSize: 22, fontWeight: 700, color: '#071C50' }}>{tongSo}</div>
                                    </div>
                                    <div style={{ ...cardStyle, flex: 1 }}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Số chức danh</div>
                                        <div style={{ fontSize: 22, fontWeight: 700, color: '#071C50' }}>{chartData.length}</div>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 6
                                    }}
                                >
                                    {chartData.map((d, i) => {
                                        const pct =
                                            tongSo > 0
                                                ? ((d.soLuongNhanVien / tongSo) * 100).toFixed(1)
                                                : '0.0';
                                        return (
                                            <div
                                                key={d.tenChucVu}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 7,
                                                    background: '#f5f7fb',
                                                    borderRadius: 6,
                                                    padding: '5px 10px',
                                                    fontSize: 13,
                                                    minWidth: 0
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        background: COLORS[i % COLORS.length],
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        color: '#444',
                                                        flex: 1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={d.tenChucVu}
                                                >
                                                    {d.tenChucVu}
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#071C50', flexShrink: 0 }}>
                                                    {d.soLuongNhanVien}
                                                </span>
                                                <span style={{ color: '#888', fontSize: 11, flexShrink: 0 }}>
                                                    ({pct}%)
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                )}
            </div>
        </Col>
    );
};

export default ThongKeNhanSuChucDanhTrangChu;